// Vendor NPC "sell" panel — modelled on the bank storage interface. LEFT = the vendor's offer panel
// (items you've staged to sell), RIGHT = your bag. Drag a bag item into the offer (or click it) to
// stage it; click a staged item to take it back. "Trade" sells everything staged for cash at the
// vendor's per-item price. Staging is client-only until you Trade (the server is authoritative).

import { INVENTORY_COLS, npcSellValue, ITEM_NAMES, type InventorySlot } from "@ghostland/shared";
import { inventoryStore } from "../items/inventoryStore.js";
import { itemThumb } from "../items/itemThumb.js";
import { injectBillStyle, cashIcon } from "./pixelBills.js";

export interface SellActions { sell: (slots: number[]) => void; }
let actions: SellActions | null = null;
export function setSellActions(a: SellActions): void { actions = a; }

const STYLE_ID = "ghostland-sell-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-sell-overlay {
  position: fixed; inset: 0; z-index: 30; display: none;
  align-items: center; justify-content: center; background: rgba(0,0,0,.7); color: #e8e8e8;
}
#gl-sell-overlay, #gl-sell-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }
#gl-sell-overlay.open { display: flex; }
#gl-sell-card { width: 540px; padding: 20px 18px 16px; background: #000; border: 2px solid #f4f4f4; image-rendering: pixelated; }
#gl-sell-card h1 { margin: 0 0 2px; font-size: 11px; letter-spacing: 2px; color: #f4f4f4; }
#gl-sell-card .hint { font-size: 7px; color: #7a7a7a; letter-spacing: 1px; margin-bottom: 14px; }
.gl-sell-cols { display: flex; gap: 18px; }
.gl-sell-col { flex: 1; }
.gl-sell-col h2 { margin: 0 0 8px; font-size: 8px; letter-spacing: 1px; color: #b0b0b0; }
.gl-sell-grid { display: grid; gap: 4px; grid-template-columns: repeat(5, 1fr); }
.gl-sell-cell {
  width: 100%; aspect-ratio: 1; background: #0c0c0c; border: 1px solid #2a2a2a;
  display: flex; align-items: flex-end; justify-content: flex-end; position: relative; cursor: pointer;
}
.gl-sell-cell.empty { cursor: default; }
.gl-sell-cell.unsellable { opacity: .3; cursor: not-allowed; }
.gl-sell-cell.staged { opacity: .28; }
.gl-sell-cell:not(.empty):not(.unsellable):hover { border-color: #f4f4f4; }
.gl-sell-cell canvas { position: absolute; inset: 0; width: 100%; height: 100%; image-rendering: pixelated; }
.gl-sell-cell .q { position: relative; font-size: 8px; color: #f4f4f4; padding: 0 2px 1px; text-shadow: 1px 1px 0 #000; }
.gl-sell-cell .val { position: absolute; top: 1px; left: 2px; right: 1px; font-size: 10px; font-weight: 700; color: #7bd66a; text-shadow: 1px 1px 0 #000, -1px 0 0 #000, 0 -1px 0 #000; }
#gl-sell-offer { border-color: #3a3a3a; }
#gl-sell-offer.drop-hint { border: 1px dashed #7bd66a; }
#gl-sell-bar { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; padding-top: 12px; border-top: 1px solid #1c1c1c; }
#gl-sell-total { font-size: 13px; color: #d8d8d8; display: inline-flex; align-items: center; gap: 6px; }
#gl-sell-total b { color: #7bd66a; }
.gl-sell-btn { background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; padding: 7px 14px; cursor: pointer; font-size: 8px; letter-spacing: 1px; image-rendering: pixelated; }
.gl-sell-btn:hover:not(:disabled) { background: #7bd66a; color: #000; border-color: #7bd66a; }
.gl-sell-btn:disabled { opacity: .35; cursor: default; }
#gl-sell-close { margin-top: 10px; width: 100%; padding: 7px 0; background: #000; border: 2px solid #6a6a6a; color: #b0b0b0; cursor: pointer; font-size: 8px; letter-spacing: 1px; }
#gl-sell-close:hover { background: #f4f4f4; color: #000; border-color: #f4f4f4; }
`;

let overlay: HTMLDivElement | null = null;
let offerGrid: HTMLDivElement | null = null;
let bagGrid: HTMLDivElement | null = null;
let totalEl: HTMLDivElement | null = null;
let tradeBtn: HTMLButtonElement | null = null;
let unsub: Array<() => void> = [];
const staged = new Set<number>(); // bag slotIndexes staged to sell
/** Per-open panel config: what this vendor BUYS (null = anything with a sell value) + labels. */
let vendorAccept: ((itemId: string) => boolean) | null = null;
let vendorTitle = "VENDOR";
let vendorRefuse = "The vendor won't buy this";

/** The per-item price THIS vendor pays (0 = won't take it). */
function vendorValue(itemId: string): number {
  if (vendorAccept && !vendorAccept(itemId)) return 0;
  return npcSellValue(itemId);
}

function bagBySlot(): Map<number, InventorySlot> {
  return new Map(inventoryStore.get().map((s) => [s.slotIndex, s]));
}

function stagedTotal(by: Map<number, InventorySlot>): number {
  let t = 0;
  for (const i of staged) { const s = by.get(i); if (s) t += vendorValue(s.itemId) * s.quantity; }
  return t;
}

function thumbCell(slot: InventorySlot, valuePer: number): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "gl-sell-cell";
  el.appendChild(itemThumb(slot.itemId));
  if (slot.quantity > 1) { const q = document.createElement("span"); q.className = "q"; q.textContent = String(slot.quantity); el.appendChild(q); }
  if (valuePer > 0) { const v = document.createElement("span"); v.className = "val"; v.textContent = `$${valuePer}`; el.appendChild(v); }
  return el;
}

function refresh(): void {
  const by = bagBySlot();
  // prune staged slots no longer present (e.g. after a successful Trade)
  for (const i of [...staged]) if (!by.has(i)) staged.delete(i);

  // LEFT: the offer (staged stacks) — click to take back
  if (offerGrid) {
    const cells: HTMLDivElement[] = [];
    for (const i of [...staged].sort((a, b) => a - b)) {
      const s = by.get(i)!;
      const el = thumbCell(s, vendorValue(s.itemId));
      el.addEventListener("click", () => { staged.delete(i); refresh(); });
      cells.push(el);
    }
    // pad to a minimum of 20 empty cells so the offer panel reads as a roomy drop zone
    for (let k = cells.length; k < 20; k++) { const e = document.createElement("div"); e.className = "gl-sell-cell empty"; cells.push(e); }
    offerGrid.replaceChildren(...cells);
  }

  // RIGHT: the bag — sellable items are draggable + clickable to stage
  if (bagGrid) {
    const cells: HTMLDivElement[] = [];
    for (let i = 0; i < 20; i++) {
      const s = by.get(i);
      if (!s) { const e = document.createElement("div"); e.className = "gl-sell-cell empty"; cells.push(e); continue; }
      const valuePer = vendorValue(s.itemId);
      const el = thumbCell(s, valuePer);
      if (valuePer <= 0) { el.classList.add("unsellable"); el.title = vendorRefuse; }
      else if (staged.has(i)) { el.classList.add("staged"); el.addEventListener("click", () => { staged.delete(i); refresh(); }); }
      else {
        el.draggable = true;
        el.addEventListener("dragstart", (e) => e.dataTransfer?.setData("text/plain", String(i)));
        el.addEventListener("click", () => { staged.add(i); refresh(); });
      }
      cells.push(el);
    }
    bagGrid.replaceChildren(...cells);
  }

  const total = stagedTotal(by);
  if (totalEl) totalEl.innerHTML = `${cashIcon()} <b>${total.toLocaleString()}</b>`;
  if (tradeBtn) { tradeBtn.disabled = staged.size === 0; tradeBtn.textContent = staged.size === 0 ? "TRADE" : `TRADE (${staged.size})`; }
}

function ensure(): void {
  injectBillStyle();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-sell-overlay";
  overlay.innerHTML = `
    <div id="gl-sell-card">
      <h1>VENDOR</h1>
      <div class="hint">drag items into the offer (or click) — then Trade to sell them for cash</div>
      <div class="gl-sell-cols">
        <div class="gl-sell-col"><h2>SELL OFFER</h2><div class="gl-sell-grid" id="gl-sell-offer"></div></div>
        <div class="gl-sell-col"><h2>YOUR BAG</h2><div class="gl-sell-grid" id="gl-sell-bag"></div></div>
      </div>
      <div id="gl-sell-bar">
        <div id="gl-sell-total">${cashIcon()} <b>0</b></div>
        <button class="gl-sell-btn" id="gl-sell-trade">TRADE</button>
      </div>
      <button id="gl-sell-close">Close (Esc)</button>
    </div>`;
  document.body.appendChild(overlay);
  offerGrid = overlay.querySelector("#gl-sell-offer");
  bagGrid = overlay.querySelector("#gl-sell-bag");
  totalEl = overlay.querySelector("#gl-sell-total");
  tradeBtn = overlay.querySelector("#gl-sell-trade");
  // the offer panel is a drop target for dragged bag items
  offerGrid!.addEventListener("dragover", (e) => { e.preventDefault(); offerGrid!.classList.add("drop-hint"); });
  offerGrid!.addEventListener("dragleave", () => offerGrid!.classList.remove("drop-hint"));
  offerGrid!.addEventListener("drop", (e) => {
    e.preventDefault(); offerGrid!.classList.remove("drop-hint");
    const i = Number(e.dataTransfer?.getData("text/plain"));
    const slot = bagBySlot().get(i);
    if (Number.isInteger(i) && slot && vendorValue(slot.itemId) > 0) { staged.add(i); refresh(); }
  });
  tradeBtn!.addEventListener("click", () => { if (staged.size > 0) actions?.sell([...staged]); });
  overlay.querySelector("#gl-sell-close")!.addEventListener("click", closeSell);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeSell(); });
}

export function openSell(opts?: { title?: string; accept?: (itemId: string) => boolean; refuse?: string }): void {
  ensure();
  staged.clear();
  vendorAccept = opts?.accept ?? null;
  vendorTitle = opts?.title ?? "VENDOR";
  vendorRefuse = opts?.refuse ?? "The vendor won't buy this";
  const h1 = overlay!.querySelector("h1");
  if (h1) h1.textContent = vendorTitle;
  overlay!.classList.add("open");
  unsub = [inventoryStore.subscribe(refresh)];
}

export function closeSell(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn()); unsub = [];
  staged.clear();
}

export function isSellOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
